import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

interface TrueCashRow {
  month: string; revenue: number; opNet: number; otherDebt: number;
  fox: number; shopify: number; reGap: number; draws: number; trueCash: number;
}
interface DebtRow {
  name: string; entity: string; monthly_payment: number;
  active_from: string; active_thru: string; kind: string; notes: string | null;
  balance: number | null; balance_asof: string | null;
}

const fmt = (v: number) => (v < 0 ? '−$' : '$') + Math.abs(v).toLocaleString();
const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en', { month: 'short', year: '2-digit' });
};

export default function TrueCash() {
  const now = new Date();
  const defaultEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [start, setStart] = useState('2025-11');
  const [end, setEnd] = useState(defaultEnd);
  const [rows, setRows] = useState<TrueCashRow[]>([]);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [schedule, setSchedule] = useState<DebtRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/true-cash?start=${start}&end=${end}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      setRows(j.data.rows); setBalances(j.data.balances); setSchedule(j.data.schedule || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce((a, r) => ({
    opNet: a.opNet + r.opNet, debt: a.debt + r.otherDebt + r.fox + r.shopify + r.reGap,
    draws: a.draws + r.draws, trueCash: a.trueCash + r.trueCash,
  }), { opNet: 0, debt: 0, draws: 0, trueCash: 0 });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">True Cash</h1>
          <p className="text-sm text-gray-500">Operating surplus − off-P&L debt service − owner draws. Debt schedule lives in the debt_schedule table.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={start} onChange={e => setStart(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="month" value={end} onChange={e => setEnd(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <button onClick={load} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ['Operating surplus', totals.opNet],
          ['Debt service', -totals.debt],
          ['Owner draws', -totals.draws],
          ['TRUE CASH', totals.trueCash],
        ].map(([label, v]) => (
          <div key={label as string} className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className={`text-2xl font-bold ${(v as number) >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(v as number)}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Month', 'Revenue', 'Op surplus', 'TGGS/other', 'Fox', 'Shopify Cap', 'RE gap', 'Draws', 'True cash'].map(h => (
                <th key={h} className="px-3 py-2 text-right first:text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => (
              <tr key={r.month} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{monthLabel(r.month)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.revenue)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.opNet)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(-r.otherDebt)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(-r.fox)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(-r.shopify)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(-r.reGap)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(-r.draws)}</td>
                <td className={`px-3 py-2 text-right font-bold ${r.trueCash >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(r.trueCash)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2">Live MCA balances</h3>
          {Object.entries(balances).map(([name, bal]) => (
            <div key={name} className="flex justify-between py-1 border-b border-gray-100 text-sm">
              <span>{name}</span><span className="font-medium">{fmt(bal)}</span>
            </div>
          ))}
          {Object.keys(balances).length === 0 && <p className="text-sm text-gray-400">No linked liability accounts found.</p>}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2">Debt schedule</h3>
          <div className="text-sm space-y-1">
            {schedule.map(d => {
              const liveBal = balances[d.name];
              const bal = liveBal !== undefined ? liveBal : (d.balance != null ? Number(d.balance) : null);
              return (
                <div key={d.name} className="flex justify-between py-1 border-b border-gray-100">
                  <span>{d.name} <span className="text-gray-400 text-xs">({d.entity})</span></span>
                  <span className="text-right">
                    <span className="text-gray-600 block">{Number(d.monthly_payment) > 0 ? fmt(Number(d.monthly_payment)) + '/mo' : 'variable'} · thru {d.active_thru.slice(0, 7)}</span>
                    {bal !== null && (
                      <span className="text-xs text-gray-400 block">
                        bal {fmt(bal)}{liveBal !== undefined ? ' (live)' : d.balance_asof ? ` as of ${d.balance_asof.slice(0, 7)}` : ''}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-400">
        Note: unreconciled months understate operating surplus (unbooked revenue/costs). Shopify Capital = 25% of that month's net Shopify sales.
        Fox weekly $7,211.55 shown as monthly equivalent. Trade-in escrow float and Callaway payable movements are not included here — see the cash flow statement.
      </p>
    </div>
  );
}
